import type { InputRef, TableColumnsType } from 'antd'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import type { AxiosResponse } from 'axios'
import type { Dayjs } from 'dayjs'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'

import {
  CaretDownOutlined,
  CaretRightOutlined,
  EllipsisOutlined,
  PaperClipOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Form,
  Input,
  List,
  Space,
  Table,
  Tag,
  Typography,
  notification as notify
} from 'antd'
import cx from 'classnames'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import { useDebounce } from 'react-use'

import $api, { API_URL } from '../../API'
import {
  NotificationEventType,
  mapper as notificationEventTypeMapper
} from '../../domain/notification-event-type.enum'
import {
  NotificationStatus,
  mapper as notificationStatusMapper
} from '../../domain/notification-status.enum'
import { Role } from '../../domain/role.enum'
import { useAppSelector } from '../../store'

const { Text } = Typography

function formatDate(date: Date): string {
  return format(date, 'MM/dd/yyyy HH:mm')
}

function truncate(input: string, length: number): string {
  console.info({ input })
  if (input.length <= length) {
    return input
  }

  return `${input.slice(0, length)}…`
}

interface ContentRange {
  content: string
  pageSize: number
  currentPage: number
  total: {
    filtered: number
    unfiltered: number
  }
}

function parseContentRangeHeaders(response: AxiosResponse): ContentRange {
  const contentRageHeader =
    response?.headers?.['Content-Range'] || response?.headers?.['content-range']
  const totalsHeader = response?.headers?.['X-Total-Count'] || response?.headers?.['x-total-count']

  console.info({ contentRageHeader, totalsHeader })

  const pattern = /^(.+?)\s+(\d+?)-(\d+?)\/(\d+)$/i
  const match = contentRageHeader?.match(pattern)

  if (!match) {
    throw new Error('Invalid Content-Range header')
  }

  const [, content, start, end, count] = match

  const pageSize = Number.parseInt(end, 10) - Number.parseInt(start, 10)

  const currentPage = Math.floor(Number.parseInt(start, 10) / pageSize)

  return {
    content: content!,
    pageSize,
    currentPage,
    total: {
      filtered: Number.parseInt(count, 10),

      unfiltered: Number.parseInt(totalsHeader ?? count!, 10)
    }
  }
}

interface Notification {
  id: string

  subject: string
  content: string

  recipients: Recipient[]
  attachments: Attachment[]

  groups: Group[]
  locations: Location[]

  event_type: NotificationEventType
  status: NotificationStatus

  scheduled_for: string
  sent_at: string | null
  read_at: string | null

  error: Record<string, unknown> | null
  failed_at: string | null

  job_id: string | null

  createdAt: Date
  updatedAt: Date
}

interface Recipient {
  id: string

  name: string | null
  address: string

  created_at: string
  updated_at: string
}

interface Attachment {
  id: string

  key: string

  created_at: string
  updated_at: string
}

const eventTypeColorMapper: Record<NotificationEventType, string> = {
  [NotificationEventType.CheckRegisterReportCreated]: 'green',
  [NotificationEventType.NetworkCheckRegisterReportCreated]: 'green',
  [NotificationEventType.AllPaymentFilesNotComplete]: 'red',
  [NotificationEventType.ConsolidatedSingleErrors]: 'orange',
  [NotificationEventType.GroupScheduleError]: 'red',
  [NotificationEventType.GroupExpirationAlert]: 'orange',
  [NotificationEventType.NomiStatusReportReceived]: 'green',

  // TODO: zabelin left it for backward compatibility, remove after approval
  [NotificationEventType.ZelisSinglePaymentType]: 'orange',
  [NotificationEventType.LuminxSingleProvider]: 'orange'
}

const statusColorMapper: Record<NotificationStatus, string> = {
  [NotificationStatus.Scheduled]: 'cyan',
  [NotificationStatus.Sent]: 'blue',
  [NotificationStatus.Failed]: 'red',
  [NotificationStatus.Cancelled]: 'default',
  [NotificationStatus.Read]: 'green',
  [NotificationStatus.Unknown]: 'default'
}

const locationColorMapper: Record<string, string> = {
  '003': 'blue',
  '001': 'red',
  '002': 'green',
  '004': 'purple'
}

interface Location {
  id: string

  key: string
  name: string
  short_name: string
  location: string

  created_at: string
  updated_at: string
}

interface Group {
  id: string

  name: string
  number: string
  location_id: string

  created_at: string
  updated_at: string
}

interface QueryData {
  notifications: Notification[]
  contentRange: ContentRange
}

type QueryContextKey = [
  string,
  State['groupsSearch'],
  State['recipientsSearch'],
  State['filters'],
  State['pagination'],
  State['sort']
]

interface State {
  expandedRowId?: string

  groupsSearch: string
  recipientsSearch: string

  filters: {
    locations: string[]
    eventTypes: NotificationEventType[]
    statuses: NotificationStatus[]
    attachments?: boolean

    createdAt: {
      from?: string
      to?: string
    }

    scheduledFor: {
      from?: string
      to?: string
    }

    sentAt: {
      from?: string
      to?: string
    }
  }

  pagination: {
    pageSize: number
    currentPage: number
  }

  sort: {
    field?: SortField
    order?: SortOrder
  }

  forcedFilters?: Partial<State['filters']>
}

type SortField = 'created_at' | 'scheduled_for' | 'sent_at' | 'event_type' | 'status'
type SortOrder = 'asc' | 'desc'

type Action =
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse' }
  | { type: 'groupsSearch/set'; payload: string }
  | { type: 'recipientsSearch/set'; payload: string }
  | { type: 'filters/set'; payload: Partial<State['filters']> }
  | { type: 'sort/set'; payload: State['sort'] }
  | { type: 'pagination/set'; payload: State['pagination'] }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'row/expand': {
      return {
        ...state,
        expandedRowId: action.payload
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowId: undefined
      }
    }

    case 'recipientsSearch/set': {
      return {
        ...state,
        recipientsSearch: action.payload
      }
    }

    case 'groupsSearch/set': {
      return {
        ...state,
        groupsSearch: action.payload
      }
    }

    case 'filters/set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
          ...state.forcedFilters
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        sort: action.payload
      }
    }

    case 'pagination/set': {
      return {
        ...state,
        pagination: action.payload
      }
    }

    default: {
      return state
    }
  }
}

interface Props {
  title: string
  locations: Location[]
  defaultPageSize?: number
  forceFilters?: Partial<State['filters']>
}

function Notifications({ title, locations, defaultPageSize, forceFilters }: Props): JSX.Element {
  const user = useAppSelector(state => state.app.user)

  const has = useCallback(
    (roles: Role[]): boolean => {
      return roles.some(role => user.roles.includes(role))
    },
    [user]
  )

  const [state, dispatch] = useReducer(reducer, {
    groupsSearch: '',
    recipientsSearch: '',
    filters: {
      locations: [],
      eventTypes: [],
      statuses: [],
      attachments: undefined,
      createdAt: {
        from: dayjs().startOf('isoWeek').startOf('day').toISOString(),
        to: dayjs().endOf('isoWeek').endOf('day').toISOString()
      },
      scheduledFor: {},
      sentAt: {},
      ...forceFilters
    },
    pagination: {
      pageSize: defaultPageSize ?? 10,
      currentPage: 0
    },
    sort: {
      field: 'created_at',
      order: 'desc'
    },
    forcedFilters: forceFilters
  })

  const [debouncedGroupsSearch, setDebouncedGroupsSearch] = useState(state.groupsSearch)
  const [debouncedRecipientsSearch, setDebouncedRecipientsSearch] = useState(state.recipientsSearch)
  const [debouncedLoadingStatus, setDebouncedLoadingStatus] = useState(false)

  useDebounce(
    () => {
      setDebouncedGroupsSearch(state.groupsSearch.toLowerCase().trim())
    },
    500,
    [state.groupsSearch]
  )

  useDebounce(
    () => {
      setDebouncedRecipientsSearch(state.recipientsSearch.toLowerCase().trim())
    },
    500,
    [state.recipientsSearch]
  )

  const recipientsSearchInputRef = useRef<InputRef>(null)
  const groupsSearchInputRef = useRef<InputRef>(null)

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'notifications',
      debouncedGroupsSearch,
      debouncedRecipientsSearch,
      state.filters,
      state.pagination,
      state.sort
    ],

    async queryFn(context) {
      const [, groupsSearch, recipientsSearch, filters, pagination, sort] = context.queryKey
      const { pageSize, currentPage } = pagination

      const searchParams = new URLSearchParams([
        ...(groupsSearch.length > 0 ? [['groupsSearch', groupsSearch]] : []),
        ...(recipientsSearch.length > 0 ? [['recipientsSearch', recipientsSearch]] : []),
        ['page-size', pageSize.toString(10)],
        ...(currentPage > 0 ? [['page', currentPage.toString(10)]] : []),
        ...(typeof sort.field === 'string' ? [['order-by', sort.field]] : []),
        ...(typeof sort.order === 'string' ? [['direction', sort.order]] : []),
        ...filters.locations.map(locationId => ['location', locationId]),
        ...filters.eventTypes.map(eventType => ['event-type', eventType]),
        ...filters.statuses.map(status => ['status', status]),
        ...(typeof filters.attachments === 'boolean'
          ? [['with-attachments', filters.attachments.toString()]]
          : []),
        ...(typeof filters.createdAt.from === 'string'
          ? [['created-at:from', filters.createdAt.from]]
          : []),
        ...(typeof filters.createdAt.to === 'string'
          ? [['created-at:to', filters.createdAt.to]]
          : []),
        ...(typeof filters.scheduledFor.from === 'string'
          ? [['scheduled-for:from', filters.scheduledFor.from]]
          : []),
        ...(typeof filters.scheduledFor.to === 'string'
          ? [['scheduled-for:to', filters.scheduledFor.to]]
          : []),
        ...(typeof filters.sentAt.from === 'string' ? [['sent-at:from', filters.sentAt.from]] : []),
        ...(typeof filters.sentAt.to === 'string' ? [['sent-at:to', filters.sentAt.to]] : [])
      ])

      const response = await $api.get(`/notifications?${searchParams.toString()}`)

      const contentRange = parseContentRangeHeaders(response)
      const notifications = response.data

      console.info({ contentRange, notifications })

      return {
        notifications,
        contentRange
      }
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false
  })

  const { refetch } = query

  useDebounce(
    () => {
      setDebouncedLoadingStatus(query.isFetching)
    },
    query.isFetching ? 500 : 0,
    [query.isFetching]
  )

  const handleExpand = useCallback((expanded: boolean, entry: Notification) => {
    if (!expanded) {
      dispatch({ type: 'row/collapse' })
      return
    }

    dispatch({ type: 'row/expand', payload: entry.id })
  }, [])

  const columns = useMemo<TableColumnsType<Notification>>(
    () => [
      {
        title: 'Created at',
        dataIndex: 'created_at',
        key: 'created_at',
        width: '8%',
        className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',

        sorter: true,
        sortOrder:
          state.sort.field === 'created_at'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,

        defaultSortOrder: 'descend',
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true,
        filterDropdown: DateFilterDropdown,

        filteredValue:
          state.filters.createdAt.from || state.filters.createdAt.to
            ? [state.filters.createdAt.from ?? '', state.filters.createdAt.to ?? '']
            : [],

        render(createdAt: string, _notification, _index) {
          return (
            <span className='block text-xs text-center align-middle'>
              {formatDate(new Date(createdAt))}
            </span>
          )
        }
      },
      {
        title: 'Scheduled',
        dataIndex: 'scheduled_for',
        key: 'scheduled_for',
        width: '8%',
        className: 'show-default-sort text-xs [&_.ant-table-column-title]:text-center',

        sorter: true,
        sortOrder:
          state.sort.field === 'scheduled_for'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,

        defaultSortOrder: 'descend',
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true,
        filterDropdown: DateFilterDropdown,

        filteredValue:
          state.filters.scheduledFor.from || state.filters.scheduledFor.to
            ? [state.filters.scheduledFor.from ?? '', state.filters.scheduledFor.to ?? '']
            : [],

        render(scheduledFor: string | undefined, _notification, _index) {
          if (!scheduledFor) {
            return <span className='block text-xs text-center text-gray-400 align-middle'>—</span>
          }

          return (
            <span className='block text-xs text-center align-middle'>
              {formatDate(new Date(scheduledFor))}
            </span>
          )
        }
      },
      {
        title: 'Sent at',
        dataIndex: 'sent_at',
        key: 'sent_at',
        width: '7%',
        className: 'show-default-sort text-xs text-center [&_.ant-table-column-title]:text-center',

        sorter: true,
        sortOrder:
          state.sort.field === 'sent_at'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,

        defaultSortOrder: 'descend',
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true,
        filterDropdown: DateFilterDropdown,

        filteredValue:
          state.filters.sentAt.from || state.filters.sentAt.to
            ? [state.filters.sentAt.from ?? '', state.filters.sentAt.to ?? '']
            : [],

        render(sentAt: string | undefined, _notification, _index) {
          if (!sentAt) {
            return <span className='block text-xs text-center text-gray-400 align-middle'>—</span>
          }

          return (
            <span className='block text-xs text-center align-middle'>
              {formatDate(new Date(sentAt))}
            </span>
          )
        }
      },
      {
        title: 'Locations',
        dataIndex: 'locations',
        key: 'locations',
        width: '8%',
        className: 'text-xs',

        ...(forceFilters?.locations === undefined
          ? {
              filters: locations.map(location => ({
                text: location.short_name,
                value: location.id
              })),
              filterMultiple: true,
              filteredValue: state.filters.locations
            }
          : {}),

        render(locations: Location[] | undefined) {
          if (!locations) {
            return <Text type='secondary'>—</Text>
          }

          return (
            <Space direction='horizontal' size='small' className='justify-center w-full' wrap>
              {locations.map(location => (
                <Tag
                  key={location.id}
                  color={locationColorMapper[location.key]}
                  title={location.name}
                >
                  {location.short_name}
                </Tag>
              ))}
            </Space>
          )
        }
      },
      {
        title: 'Groups',
        dataIndex: 'groups',
        key: 'groups',
        width: '10%',
        className: 'text-xs',
        filteredValue: state.groupsSearch.length > 0 ? [state.groupsSearch] : undefined,

        filterDropdown({ selectedKeys, setSelectedKeys, close, confirm }) {
          return (
            <div
              className='flex flex-col items-end p-2'
              onKeyDown={event => {
                event.stopPropagation()
              }}
            >
              <Input
                ref={groupsSearchInputRef}
                placeholder='Search groups'
                value={selectedKeys[0] ?? ''}
                onChange={event => {
                  setSelectedKeys([event.target.value])
                }}
                onPressEnter={event => {
                  confirm()
                  close()
                  event.preventDefault()
                  event.stopPropagation()
                }}
                className='block mb-2 w-full'
              />
              <Space>
                <Button
                  onClick={() => {
                    setSelectedKeys(['', Date.now()])
                    confirm()
                    close()
                  }}
                  size='small'
                >
                  Reset
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    confirm()
                    close()
                  }}
                >
                  Apply
                </Button>
              </Space>
            </div>
          )
        },

        filterIcon() {
          return (
            <SearchOutlined
              className={cx({
                'text-[#1890ff]': state.groupsSearch.length > 0,
                'animate-pulse': query.isLoading && state.groupsSearch.length > 0
              })}
            />
          )
        },

        onFilterDropdownOpenChange(visible) {
          if (visible) {
            setTimeout(() => {
              groupsSearchInputRef.current?.select()
            }, 100)
          }
        },

        render(groups: Group[] | undefined, _notification, _index) {
          if (!groups) {
            return <Text type='secondary'>—</Text>
          }

          let groupsToShow = groups.map(group => (
            <Tag key={group.id} title={group.name}>
              {group.number}
            </Tag>
          ))

          if (groupsToShow.length > 3) {
            groupsToShow = groupsToShow.slice(0, 3)
            groupsToShow.push(
              <Tag
                key='3_groups_more_indicator'
                style={{ borderStyle: 'none', backgroundColor: 'transparent', fontSize: '20px' }}
              >
                {'\u00B7\u00B7\u00B7'}
              </Tag>
            )
          }

          return (
            <Space direction='horizontal' size='small' className='justify-center w-full' wrap>
              {groupsToShow}
            </Space>
          )
        }
      },
      {
        title: 'Recipients',
        dataIndex: 'recipients',
        key: 'recipients',
        width: '20%',
        className: 'text-xs',

        filteredValue: state.recipientsSearch.length > 0 ? [state.recipientsSearch] : undefined,

        filterDropdown({ selectedKeys, setSelectedKeys, close, confirm }) {
          return (
            <div
              className='flex flex-col items-end p-2'
              onKeyDown={event => {
                event.stopPropagation()
              }}
            >
              <Input
                ref={recipientsSearchInputRef}
                placeholder='Search recipients'
                value={selectedKeys[0] ?? ''}
                onChange={event => {
                  setSelectedKeys([event.target.value, Date.now()])
                }}
                onPressEnter={event => {
                  confirm()
                  close()
                  event.preventDefault()
                  event.stopPropagation()
                }}
                className='block mb-2 w-full'
              />
              <Space>
                <Button
                  onClick={() => {
                    setSelectedKeys(['', Date.now()])
                    confirm()
                    close()
                  }}
                  size='small'
                >
                  Reset
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    confirm()
                    close()
                  }}
                >
                  Apply
                </Button>
              </Space>
            </div>
          )
        },

        filterIcon() {
          return (
            <SearchOutlined
              className={cx({
                'text-[#1890ff]': state.recipientsSearch.length > 0,
                'animate-pulse': query.isLoading && state.recipientsSearch.length > 0
              })}
            />
          )
        },

        onFilterDropdownOpenChange(visible) {
          if (visible) {
            setTimeout(() => {
              recipientsSearchInputRef.current?.select()
            }, 100)
          }
        },

        render(value: Recipient[], _notification, _index) {
          return (
            <List
              itemLayout='horizontal'
              bordered={false}
              size='small'
              rowKey='id'
              dataSource={value}
              renderItem={(recipient, _i) => (
                <List.Item className='!p-0 !py-1'>
                  <List.Item.Meta
                    title={recipient.name ?? recipient.address}
                    description={typeof recipient.name === 'string' ? recipient.address : undefined}
                  />
                </List.Item>
              )}
            />
          )
        }
      },
      {
        title: 'Event',
        dataIndex: 'event_type',
        key: 'event_type',
        width: '8%',
        className: 'text-xs',

        ...(forceFilters?.eventTypes === undefined
          ? {
              filters: Object.keys(NotificationEventType)
                // TODO: zabelin check notification-event-type.enum.ts
                .filter(key => key !== 'LuminxSingleProvider' && key !== 'ZelisSinglePaymentType')
                .map(key => ({
                  text: notificationEventTypeMapper[
                    NotificationEventType[
                      key as keyof typeof NotificationEventType
                    ] as NotificationEventType
                  ],
                  value: NotificationEventType[key as keyof typeof NotificationEventType]
                })),
              filterMultiple: true,

              filteredValue: state.filters.eventTypes
            }
          : {}),

        render(key: NotificationEventType) {
          return (
            <Tag color={eventTypeColorMapper[key]} title={notificationEventTypeMapper[key]}>
              {truncate(notificationEventTypeMapper[key], 10)}
            </Tag>
          )
        }
      },
      {
        title: 'Status',
        key: 'status',
        dataIndex: 'status',
        width: '8%',
        className: 'text-xs',

        ...(forceFilters?.statuses === undefined
          ? {
              filters: Object.keys(NotificationStatus)
                .filter(key => {
                  const status = NotificationStatus[key as keyof typeof NotificationStatus]
                  return status !== NotificationStatus.Scheduled
                })
                .map(key => ({
                  text: notificationStatusMapper[
                    NotificationStatus[key as keyof typeof NotificationStatus] as NotificationStatus
                  ],
                  value: NotificationStatus[key as keyof typeof NotificationStatus]
                })),
              filterMultiple: true,
              filteredValue: state.filters.statuses
            }
          : {}),

        onCell(_data, _index) {
          return {
            className: 'text-center'
          }
        },

        render(status: NotificationStatus, notification, _index) {
          return (
            <Tag
              color={statusColorMapper[status]}
              title={
                status === NotificationStatus.Read && notification.read_at
                  ? `Read at ${formatDate(new Date(notification.read_at))}`
                  : status === NotificationStatus.Failed
                    ? `Failed at ${formatDate(new Date(notification.failed_at!))} with error: ${notification.error ? JSON.stringify(notification.error) : 'Something went wrong'}`
                    : // : status === NotificationStatus.Canceled
                      //   ? `Canceled at ${formatDate(new Date())} by Todd Salter`
                      undefined
              }
            >
              {notificationStatusMapper[status]}
            </Tag>
          )
        }
      },
      {
        title: 'Attachments',
        dataIndex: 'attachments',
        key: 'attachments',
        width: '5%',
        className: 'text-xs',

        ...(forceFilters?.attachments === undefined
          ? {
              filters: [
                {
                  text: 'With attachments',
                  value: 'true'
                }
              ],
              filterMultiple: true,
              filteredValue: state.filters.attachments ? ['true'] : undefined
            }
          : {}),

        render(attachments: Attachment[] | undefined, _notification, _index) {
          if (!Array.isArray(attachments) || attachments.length === 0) {
            return null
          }

          return (
            <div className='flex justify-center items-baseline w-full text-gray-400'>
              <PaperClipOutlined className='text-base' />

              <span className='pl-1 text-sm'>× {attachments.length}</span>
            </div>
          )
        }
      },
      {
        key: 'actions',
        width: '5.5rem',
        className: 'text-xs',

        render(_value, notification, _index) {
          const isDisabled = state.expandedRowId === notification.id

          return (
            <div className='flex justify-center'>
              <Dropdown
                disabled={isDisabled}
                menu={{
                  items: [
                    {
                      key: 'expand',
                      label: 'Expand',
                      onClick(_info) {
                        handleExpand(true, notification)
                      },
                      icon: <SearchOutlined />
                    },
                    ...(has([
                      Role.Administrator,
                      Role.FinanceManager,
                      Role.CheckRunManager,
                      Role.CheckRun
                    ]) &&
                    (notification.status === NotificationStatus.Sent ||
                      notification.status === NotificationStatus.Failed) &&
                    notification.event_type !== NotificationEventType.ConsolidatedSingleErrors &&
                    notification.event_type !== NotificationEventType.LuminxSingleProvider &&
                    notification.event_type !== NotificationEventType.ZelisSinglePaymentType
                      ? [
                          {
                            key: 'resend',
                            label: 'Resend',
                            onClick() {
                              $api
                                .get(`/notifications/${notification.id}/resend`)
                                .then(_job => {
                                  notify.success({
                                    message: 'Notification has been successfully resent'
                                  })
                                  void refetch()
                                })
                                .catch(() => {
                                  notify.error({
                                    message: 'Failed to resend notification',
                                    description: 'Something went wrong'
                                  })
                                })
                            },
                            icon: <SendOutlined />
                          }
                        ]
                      : []),
                    ...(notification.status === NotificationStatus.Scheduled && notification.job_id
                      ? [
                          {
                            type: 'divider' as const
                          },
                          {
                            key: 'cancel',
                            label: 'Cancel',
                            danger: true,
                            onClick() {
                              $api
                                .get(`/notifications/${notification.job_id}/cancel`)
                                .then(() => {
                                  notify.success({
                                    message: 'Notification has been successfully cancelled'
                                  })
                                  void refetch()
                                })
                                .catch(() => {
                                  notify.error({
                                    message: 'Failed to cancel notification',
                                    description: 'Something went wrong'
                                  })
                                })
                            },
                            icon: <StopOutlined />
                          }
                        ]
                      : [])
                  ]
                }}
              >
                <Button
                  htmlType='button'
                  size='large'
                  type='text'
                  icon={<EllipsisOutlined />}
                  className={cx({
                    'opacity-25 cursor-not-allowed': isDisabled
                  })}
                />
              </Dropdown>
            </div>
          )
        }
      }
    ],
    [
      state.sort.field,
      state.sort.order,
      state.groupsSearch,
      state.recipientsSearch,
      state.expandedRowId,
      state.filters,
      locations,
      forceFilters,
      query.isLoading,
      refetch,
      handleExpand,
      has
    ]
  )

  return (
    <Card className='w-full rounded-xl [&>.ant-card-body]:p-0 -mt-4' title={title}>
      <Table
        size='small'
        className='show-default-sort'
        rowKey='id'
        dataSource={query.data?.notifications ?? []}
        loading={debouncedLoadingStatus}
        columns={columns}
        expandable={{
          onExpand: handleExpand,

          rowExpandable(notification) {
            return (
              typeof notification.subject === 'string' ||
              typeof notification.content === 'string' ||
              notification.attachments.length > 0
            )
          },

          expandedRowKeys: typeof state.expandedRowId === 'string' ? [state.expandedRowId] : [],

          expandedRowRender(notification, index, indent, expanded) {
            if (!expanded) {
              return null
            }

            return <ExpandedRow notification={notification} />
          },
          expandIcon: ExpandIcon,
          columnWidth: '2rem'
        }}
        pagination={{
          size: 'default',

          pageSizeOptions: [10, 25, 50],

          current: state.pagination.currentPage + 1,
          pageSize: state.pagination.pageSize,
          total: query.data?.contentRange.total.filtered ?? 0,

          showLessItems: true,
          className: 'py-2 !pl-6 !pr-6',

          onChange(page, pageSize) {
            dispatch({
              type: 'pagination/set',
              payload: {
                currentPage: page - 1,
                pageSize
              }
            })
          },

          onShowSizeChange(page, pageSize) {
            dispatch({
              type: 'pagination/set',
              payload: {
                currentPage: page - 1,
                pageSize
              }
            })
          },

          showTotal(total, range) {
            return (
              <span className='text-sm text-gray-400'>
                Showing notifications {range[0]}–{range[1]} out of {total}
                {query.data &&
                  query.data.contentRange.total.unfiltered !== total &&
                  ` • ${query.data.contentRange.total.unfiltered} total`}
              </span>
            )
          }
        }}
        onChange={(pagination, filters, sorter, extra) => {
          if (extra.action === 'filter') {
            dispatch({
              type: 'groupsSearch/set',
              payload: filters.groups ? (filters.groups[0] as string) || '' : ''
            })

            dispatch({
              type: 'recipientsSearch/set',
              payload: filters.recipients ? (filters.recipients[0] as string) || '' : ''
            })

            dispatch({
              type: 'filters/set',
              payload: {
                locations: filters.locations?.map(value => value as string) ?? [],
                eventTypes: filters.event_type?.map(value => value as NotificationEventType) ?? [],
                statuses: filters.status?.map(value => value as NotificationStatus) ?? [],
                attachments: filters.attachments?.[0] === 'true' ? true : undefined,
                createdAt: {
                  from: filters.created_at?.[0] as string | undefined,
                  to: filters.created_at?.[1] as string | undefined
                },
                scheduledFor: {
                  from: filters.scheduled_for?.[0] as string | undefined,
                  to: filters.scheduled_for?.[1] as string | undefined
                },
                sentAt: {
                  from: filters.sent_at?.[0] as string | undefined,
                  to: filters.sent_at?.[1] as string | undefined
                }
              }
            })
          }

          if (extra.action === 'sort') {
            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

            dispatch({
              type: 'sort/set',
              payload: {
                field: singleSorter?.column?.key as SortField | undefined,
                order:
                  typeof singleSorter?.order === 'string'
                    ? ({ ascend: 'asc', descend: 'desc' }[singleSorter.order] as SortOrder)
                    : undefined
              }
            })
          }
        }}
      />
    </Card>
  )
}

const scheduledNotificationsForcedFilters: Partial<State['filters']> = {
  statuses: [NotificationStatus.Scheduled]
}

export function NotificationsPage(): JSX.Element {
  const locationsQuery = useQuery<unknown, unknown, Location[], string[]>({
    queryKey: ['locations'],

    async queryFn() {
      const response = await $api.get<Location[]>('/locations')

      return response.data
    }
  })

  return (
    <Space direction='vertical' size={48} className='p-0 w-full'>
      <Notifications
        title='Scheduled Notifications'
        locations={locationsQuery.data ?? []}
        defaultPageSize={5}
        forceFilters={scheduledNotificationsForcedFilters}
      />

      <Notifications title='Notifications' locations={locationsQuery.data ?? []} />
    </Space>
  )
}

interface ExpandedRowProps {
  notification: Notification
}

function ExpandedRow({ notification }: ExpandedRowProps): JSX.Element {
  const token = localStorage.getItem('token') ?? ''

  return (
    <div className='flex flex-col'>
      {(typeof notification.subject === 'string' || typeof notification.content === 'string') && (
        <div className='flex flex-col gap-4 p-4'>
          {typeof notification.subject === 'string' && (
            <Text className='text-sm font-semibold'>{notification.subject}</Text>
          )}

          {typeof notification.content === 'string' && (
            <div className='border' style={{ all: 'unset' }}>
              <div dangerouslySetInnerHTML={{ __html: notification.content }} />
            </div>
          )}
        </div>
      )}

      {notification.attachments.length > 0 && (
        <div className='flex flex-col gap-4 p-4'>
          <Text className='text-sm font-semibold'>Attachments</Text>

          <div className='flex flex-wrap gap-4'>
            {notification.attachments.map(attachment => (
              <a
                href={`${API_URL}/files/${encodeURIComponent(attachment.key.split('/').pop()!)}?event-type-id=34&token=${token}`}
                key={attachment.id}
                className='border flex items-baseline px-3 py-2 max-w-[14rem] gap-2 rounded'
                title={attachment.key.split('/').pop()}
              >
                <PaperClipOutlined className='text-xl text-gray-400' />

                <span className='text-gray-400 truncate'>{attachment.key.split('/').pop()}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ExpandIcon(props: RenderExpandIconProps<Notification>): JSX.Element | null {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-30 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}

export function DateFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  close: _close
}: FilterDropdownProps): JSX.Element {
  const fromRaw = selectedKeys[0] ?? undefined
  const toRaw = selectedKeys[1] ?? undefined

  const from = fromRaw ? dayjs(String(fromRaw)) : undefined

  const to = toRaw ? dayjs(String(toRaw)) : undefined

  const setFrom = useCallback(
    (input: Dayjs | null) => {
      const value = input ? input.startOf('day').toISOString() : undefined

      setSelectedKeys([value as React.Key, toRaw!])
    },
    [setSelectedKeys, toRaw]
  )

  const setTo = useCallback(
    (input: Dayjs | null) => {
      const value = input ? input.endOf('day').toISOString() : undefined

      setSelectedKeys([fromRaw!, value as React.Key])
    },
    [setSelectedKeys, fromRaw]
  )

  return (
    <div className='flex flex-col'>
      <div className='flex'>
        <Form
          layout='horizontal'
          className='flex flex-col pt-5 pr-5 pb-3 pl-5 w-full'
          labelAlign='left'
          labelCol={{
            className: 'w-12 font-semibold'
          }}
        >
          <Form.Item label='From' colon={false} className='mb-3'>
            <DatePicker
              placeholder={dayjs().subtract(1, 'week').format('YYYY-MM-DD')}
              value={from}
              onChange={setFrom}
              allowClear
            />
          </Form.Item>

          <Form.Item label='To' colon={false} className='mb-3'>
            <DatePicker
              placeholder={dayjs().subtract(1, 'day').format('YYYY-MM-DD')}
              value={to}
              onChange={setTo}
              allowClear
            />
          </Form.Item>
        </Form>

        <ul className='flex flex-col pt-4 pr-5 pb-3 pl-2 border-l border-l-gray-100'>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().startOf('day').toISOString(),
                  dayjs().endOf('day').toISOString()
                ])

                confirm()
              }}
            >
              Today
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().subtract(1, 'day').startOf('day').toISOString(),
                  dayjs().subtract(1, 'day').endOf('day').toISOString()
                ])

                confirm()
              }}
            >
              Yesterday
            </Button>
          </li>
          <li className='pt-1.5'>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().startOf('isoWeek').startOf('day').toISOString(),
                  dayjs().endOf('isoWeek').endOf('day').toISOString()
                ])

                confirm()
              }}
            >
              This week
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().subtract(1, 'week').startOf('isoWeek').startOf('day').toISOString(),
                  dayjs().subtract(1, 'week').endOf('isoWeek').endOf('day').toISOString()
                ])

                confirm()
              }}
            >
              Last week
            </Button>
          </li>
          <li className='pt-1.5'>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().startOf('month').startOf('day').toISOString(),
                  dayjs().endOf('month').endOf('day').toISOString()
                ])

                confirm()
              }}
            >
              This month
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().subtract(1, 'month').startOf('month').startOf('day').toISOString(),
                  dayjs().subtract(1, 'month').endOf('month').endOf('day').toISOString()
                ])

                confirm()
              }}
            >
              Last month
            </Button>
          </li>
        </ul>
      </div>

      <div className='flex justify-end items-center p-3 border-t border-t-gray-100'>
        <Space direction='horizontal'>
          <Button
            onClick={() => {
              setSelectedKeys([])
              confirm()
            }}
            size='small'
          >
            Reset
          </Button>

          <Button
            type='primary'
            size='small'
            onClick={() => {
              confirm()
            }}
          >
            Apply
          </Button>
        </Space>
      </div>
    </div>
  )
}
